import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  App,
  Alert,
  Typography,
  Divider,
  Row,
  Col
} from 'antd';
import { trpc as api } from '@/lib/api';
import { TemplateSelector } from '../schedule/TemplateSelector';
import type { WorkOrderTemplate } from '../schedule/TemplateSelector';
import dayjs from 'dayjs';
import { withDevModalFilePath, NumericInput, RichTextEditor } from '../common';

const { Modal } = withDevModalFilePath('client/src/components/emergency/EmergencyWorkCreateModal.tsx');

const { Title } = Typography;

interface EmergencyWorkCreateModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

type EquipmentOption = {
  id: string;
  name: string;
  code?: string | null;
};

type EquipmentListResponse = {
  data?: EquipmentOption[];
  items?: EquipmentOption[];
  total?: number;
  pagination?: { total?: number };
};

interface FormValues {
  title: string;
  description?: string;
  equipmentId: string;
  workOrderTemplateId?: string;
  impactLevel: 'critical' | 'major' | 'minor';
  affectedAreas?: string;
  estimatedDowntime?: number;
  priority: 'urgent' | 'high';
  reportedAt: dayjs.Dayjs;
  emergencyNotes?: string;
}

export const EmergencyWorkCreateModal: React.FC<EmergencyWorkCreateModalProps> = ({
  open,
  onCancel,
  onSuccess
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [selectedTemplate, setSelectedTemplate] = useState<WorkOrderTemplate | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: equipmentList, isLoading: isEquipmentLoading } = api.equipment.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: open }
  );

  const equipmentOptions = useMemo(() => {
    const list = equipmentList as EquipmentListResponse | undefined;
    const items = list?.data ?? list?.items ?? [];
    return items.map((item) => ({
      label: `${item.code ?? ''} ${item.name}`.trim(),
      value: item.id,
    }));
  }, [equipmentList]);

  // 緊急作業作成ミューテーション
  const createEmergencyMutation = api.schedule.createEmergency.useMutation({    
    onSuccess: () => {
      message.success('緊急作業を報告しました');
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      setSubmitError(error.message || '報告に失敗しました');
      message.error('緊急作業の報告に失敗しました');
    }
  });

  // モーダルが開かれた時の初期化
  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedTemplate(null);
      setSubmitError(null);
      // デフォルト値を設定
      form.setFieldsValue({
        reportedAt: dayjs(),
        priority: 'urgent',
        impactLevel: 'major'
      });
    }
  }, [open, form]);

  // フォームの送信
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await createEmergencyMutation.mutateAsync({
        equipmentId: values.equipmentId,
        title: values.title,
        description: values.description,
        workOrderTemplateId: selectedTemplate?.id,
        impactLevel: values.impactLevel,
        affectedAreas: values.affectedAreas,
        estimatedDowntime: values.estimatedDowntime,
        priority: values.priority,
        reportedAt: values.reportedAt.toDate(),
        emergencyNotes: values.emergencyNotes
      });
    } catch (error) {
      // バリデーションエラーの場合
      if (error instanceof Error) {
        console.error('Validation error:', error);
      }
    }
  };

  // モーダルを閉じる
  const handleClose = () => {
    form.resetFields();
    setSelectedTemplate(null);
    setSubmitError(null);
    onCancel();
  };

  return (
    <Modal
      title="緊急作業を報告"
      open={open}
      onCancel={handleClose}
      width={800}
      footer={
        <Space>
          <Button onClick={handleClose}>キャンセル</Button>
          <Button
            type="primary"
            danger
            onClick={handleSubmit}
            loading={createEmergencyMutation.isPending}
          >
            報告
          </Button>
        </Space>
      }
    >
      {submitError && (
        <Alert
          message="エラー"
          description={submitError}
          type="error"
          showIcon
          closable
          onClose={() => setSubmitError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          priority: 'urgent',
          impactLevel: 'major',
          reportedAt: dayjs()
        }}
      >
        {/* 基本情報 */}
        <Title level={5}>基本情報</Title>

        <Form.Item
          label="タイトル"
          name="title"
          rules={[{ required: true, message: 'タイトルを入力してください' }]}
        >
          <Input placeholder="例: 冷却システム緊急停止" />
        </Form.Item>

        <Form.Item
          label="詳細"
          name="description"
          getValueFromEvent={(markdown: string) => markdown}
          getValueProps={(value) => ({ value: value || '' })}
        >
          <RichTextEditor
            placeholder="障害や緊急作業の詳細を入力..."
            minHeight={100}
            showPreviewToggle={false}
          />
        </Form.Item>

        {/* 影響度・報告情報 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="報告日時"
              name="reportedAt"
              rules={[{ required: true, message: '報告日時を選択してください' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder="報告日時を選択"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="影響レベル"
              name="impactLevel"
              rules={[{ required: true, message: '影響レベルを選択してください' }]}
            >
              <Select>
                <Select.Option value="critical">
                  <span style={{ color: '#ff4d4f' }}>🔴 重大</span>
                </Select.Option>
                <Select.Option value="major">
                  <span style={{ color: '#faad14' }}>🟠 大</span>
                </Select.Option>
                <Select.Option value="minor">
                  <span style={{ color: '#fadb14' }}>🟡 小</span>
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="優先度"
              name="priority"
              rules={[{ required: true, message: '優先度を選択してください' }]}
            >
              <Select>
                <Select.Option value="urgent">緊急</Select.Option>
                <Select.Option value="high">高</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="影響範囲"
              name="affectedAreas"
            >
              <Input placeholder="例: 第1製造ライン、冷却システム全域" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="推定ダウンタイム（分）"
              name="estimatedDowntime"
            >
              <NumericInput min={0} max={10080} step={15} style={{ width: '100%' }} placeholder="例: 120" />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        {/* 対象設備・作業指示書 */}
        <Title level={5}>対象設備・作業指示書</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="対象設備"
              name="equipmentId"
              rules={[{ required: true, message: '対象設備を選択してください' }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="設備を選択"
                style={{ width: '100%' }}
                loading={isEquipmentLoading}
                options={equipmentOptions}
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="作業指示書（オプション）">
              <TemplateSelector
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        {/* 緊急メモ */}
        <Title level={5}>緊急メモ</Title>
        <Form.Item
          label="緊急時の特記事項"
          name="emergencyNotes"
          getValueFromEvent={(markdown: string) => markdown}
          getValueProps={(value) => ({ value: value || '' })}
        >
          <RichTextEditor
            placeholder="安全上の注意点、一時対応の内容などを記載..."
            minHeight={120}
            showPreviewToggle={false}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
